import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSelector } from 'react-redux';
import { selectAllWires } from '../../../redux/wireSlice';

export function useGizmoRender(scene, camera, shouldShowGizmos = true) {
  const selectedObjectIds = useSelector((state) => state.selection.selectedObjectIds);
  const wires = useSelector(selectAllWires);
  const gizmoGroupRef = useRef(null);

  // ✅ Dynamic Scaling Effect
  // Eski sürüm RAF her frame'de gizmo OLMASA dahi forEach çalıştırıyor + kamera
  // değişmemiş bile olsa scale.set() yapıyordu. İkinci bir 60Hz loop olarak ana
  // render loop ile birlikte çalışmak da CPU yükünü artırıyordu. Şimdi:
  //   - Hiç gizmo yoksa erken çık (sadece RAF planla, iş yapma).
  //   - Kamera zoom/position son frame'den beri değişmediyse scale güncelleme.
  useEffect(() => {
    if (!scene || !camera) return;

    let animationFrameId;
    let lastZoom = -1;
    const lastPos = new THREE.Vector3(NaN, NaN, NaN);

    const updateScale = () => {
      const grp = gizmoGroupRef.current;
      if (grp && grp.children.length > 0) {
        let cameraChanged = false;
        if (camera.isOrthographicCamera) {
          if (camera.zoom !== lastZoom) {
            lastZoom = camera.zoom;
            cameraChanged = true;
          }
        } else if (!lastPos.equals(camera.position)) {
          lastPos.copy(camera.position);
          cameraChanged = true;
        }
        if (cameraChanged) {
          grp.children.forEach((gizmo) => {
            if (gizmo.userData?.type === 'mtextBorder') return;
            let scale = 1;
            if (camera.isOrthographicCamera) {
              scale = 2 / camera.zoom;
            } else {
              const distance = camera.position.distanceTo(gizmo.position);
              scale = distance * 0.01;
            }
            gizmo.scale.set(scale, scale, scale);
          });
        }
      }
      animationFrameId = requestAnimationFrame(updateScale);
    };

    updateScale();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [scene, camera]);

  useEffect(() => {
    if (!scene) return;

    // Önceki gizmoları temizle
    if (gizmoGroupRef.current) {
      scene.remove(gizmoGroupRef.current);
      gizmoGroupRef.current.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();

      });
      gizmoGroupRef.current = null;
    }

    // ✅ 20'den fazla nesne seçiliyse gizmo gösterme (performans optimizasyonu)
    if (!shouldShowGizmos) return;

    if (selectedObjectIds.length === 0) return;

    const gizmoGroup = new THREE.Group();
    gizmoGroup.name = "__gizmoGroup";

    // ✅ Helper: Bir nesnenin hatch'in çocuğu olup olmadığını kontrol et
    const isChildOfHatch = (obj) => {
      let parent = obj.parent;
      while (parent) {
        if (parent.userData?.type === 'hatch' || parent.userData?.isPatternHatch) {
          return true;
        }
        parent = parent.parent;
      }
      return false;
    };

    // ✅ Helper: Bir nesnenin block'un çocuğu olup olmadığını kontrol et
    const isChildOfBlock = (obj) => {
      let parent = obj.parent;
      while (parent) {
        if (parent.userData?.type === 'block') {
          return true;
        }
        parent = parent.parent;
      }
      return false;
    };

    // ✅ Helper: Gizmo oluştur
    const createGizmo = (position, color, name, userData) => {
      const geometry = new THREE.PlaneGeometry(4, 4);
      const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.8,
        depthTest: false,
      });
      const gizmo = new THREE.Mesh(geometry, material);
      gizmo.position.copy(position);
      gizmo.name = name;
      gizmo.renderOrder = 999;
      gizmo.userData = { isGizmo: true, ...userData };
      return gizmo;
    };

    // ✅ Seçili hatch'ler için basePoint gizmoları
    scene.traverse((obj) => {
      const hatchId = obj.userData?.id || obj.userData?.dxfId;
      if (obj.userData?.type === 'hatch' && hatchId && selectedObjectIds.includes(hatchId)) {
        const worldPos = new THREE.Vector3();
        obj.getWorldPosition(worldPos);
        gizmoGroup.add(createGizmo(worldPos, 0xff00ff, '__gizmo_hatch_basepoint', {
          type: "hatchBasePoint",
          hatchId: hatchId,
          objUUID: obj.uuid
        }));
      }
    });

    // ✅ Seçili BLOCK'lar için basePoint gizmoları (sadece 1 gizmo)
    scene.traverse((obj) => {
      if (obj.userData?.type === 'block' && selectedObjectIds.includes(obj.userData.id)) {
        const worldPos = new THREE.Vector3();
        if (obj.matrixAutoUpdate === false) {
          // OCS block: pozisyon matrix translation'da
          worldPos.setFromMatrixPosition(obj.matrix);
          // Parent varsa world space'e dönüştür
          if (obj.parent) {
            obj.parent.updateWorldMatrix(true, false);
            worldPos.applyMatrix4(obj.parent.matrixWorld);
          }
        } else {
          // Normal block: position + parent chain
          obj.getWorldPosition(worldPos);
        }
        gizmoGroup.add(createGizmo(worldPos, 0x00bfff, '__gizmo_block_basepoint', {
          type: "blockBasePoint",
          blockId: obj.userData.id,
          blockName: obj.userData.name,
          objUUID: obj.uuid
        }));
      }
    });

    // ✅ Seçili MTEXT'ler için gizmolar (4 köşe + 1 merkez + border)
    scene.traverse((obj) => {
      if (obj.userData?.type === 'mtext' && selectedObjectIds.includes(obj.userData.id)) {
        let corners, center;
        if (obj.isSprite) {
          const p = obj.position;
          const sx = obj.scale.x, sy = obj.scale.y;
          corners = [
            new THREE.Vector3(p.x, p.y, 0),
            new THREE.Vector3(p.x + sx, p.y, 0),
            new THREE.Vector3(p.x + sx, p.y - sy, 0),
            new THREE.Vector3(p.x, p.y - sy, 0),
          ];
          center = new THREE.Vector3(p.x + sx / 2, p.y - sy / 2, 0);
        } else {
          obj.updateWorldMatrix(true, false);
          const box = new THREE.Box3().setFromObject(obj);
          if (box.isEmpty()) return;
          corners = [
            new THREE.Vector3(box.min.x, box.max.y, 0),
            new THREE.Vector3(box.max.x, box.max.y, 0),
            new THREE.Vector3(box.max.x, box.min.y, 0),
            new THREE.Vector3(box.min.x, box.min.y, 0),
          ];
          center = box.getCenter(new THREE.Vector3());
        }

        // 4 köşe gizmo (yeşil)
        corners.forEach((pos, idx) => {
          gizmoGroup.add(createGizmo(pos, 0x00ff00, `__gizmo_mtext_corner_${idx}`, {
            type: "mtextCorner", cornerIndex: idx,
            mtextId: obj.userData.id, objUUID: obj.uuid
          }));
        });

        // Merkez gizmo (kırmızı)
        gizmoGroup.add(createGizmo(center, 0xff0000, '__gizmo_mtext_center', {
          type: "mtextCenter",
          mtextId: obj.userData.id, objUUID: obj.uuid
        }));

        // Textbox border (dashed cyan)
        const borderGeom = new THREE.BufferGeometry().setFromPoints([
          corners[0], corners[1], corners[2], corners[3], corners[0]
        ]);
        const borderMat = new THREE.LineDashedMaterial({
          color: 0x00bfff,
          dashSize: 3,
          gapSize: 2,
          transparent: true,
          opacity: 0.7,
          depthTest: false,
        });
        const borderLine = new THREE.Line(borderGeom, borderMat);
        borderLine.computeLineDistances();
        borderLine.name = '__gizmo_mtext_border';
        borderLine.renderOrder = 998;
        borderLine.userData = {
          isGizmo: true,
          type: "mtextBorder",
          mtextId: obj.userData.id, objUUID: obj.uuid
        };
        gizmoGroup.add(borderLine);
      }
    });

    // ✅ Seçili CIRCLE'lar için gizmolar (merkez + 4 quarter point = 5 gizmo)
    scene.traverse((obj) => {
      if (obj.userData?.type === 'circle' && selectedObjectIds.includes(obj.userData.id)) {
        const center = obj.userData.center || { x: 0, y: 0 };
        const radius = obj.userData.radius || 0;

        // Merkez gizmosu
        const centerWorld = new THREE.Vector3(center.x, center.y, 0);
        obj.localToWorld(centerWorld);
        gizmoGroup.add(createGizmo(centerWorld, 0xff0000, '__gizmo_circle_center', {
          type: "circleCenter",
          circleId: obj.userData.id,
          objUUID: obj.uuid
        }));

        // Quarter point gizmoları (0°, 90°, 180°, 270°)
        const quarterAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
        quarterAngles.forEach((angle, idx) => {
          const qx = center.x + radius * Math.cos(angle);
          const qy = center.y + radius * Math.sin(angle);
          const quarterWorld = new THREE.Vector3(qx, qy, 0);
          obj.localToWorld(quarterWorld);
          gizmoGroup.add(createGizmo(quarterWorld, 0x00ffff, `__gizmo_circle_quarter_${idx}`, {
            type: "circleQuarter",
            quarterIndex: idx,
            circleId: obj.userData.id,
            objUUID: obj.uuid
          }));
        });
      }
    });

    // ✅ Seçili ARC'lar için gizmolar (merkez + start + end + midpoint = 4 gizmo)
    scene.traverse((obj) => {
      if (obj.userData?.type === 'arc' && selectedObjectIds.includes(obj.userData.id)) {
        const center = obj.userData.center || { x: 0, y: 0 };
        const radius = obj.userData.radius || 0;
        const startAngle = obj.userData.startAngle || 0; // Radyan cinsinden
        const endAngle = obj.userData.endAngle || 0; // Radyan cinsinden

        // Merkez gizmosu
        const centerWorld = new THREE.Vector3(center.x, center.y, 0);
        obj.localToWorld(centerWorld);
        gizmoGroup.add(createGizmo(centerWorld, 0xff0000, '__gizmo_arc_center', {
          type: "arcCenter",
          arcId: obj.userData.id,
          objUUID: obj.uuid
        }));

        // Start point gizmosu (açılar zaten radyan)
        const startX = center.x + radius * Math.cos(startAngle);
        const startY = center.y + radius * Math.sin(startAngle);
        const startWorld = new THREE.Vector3(startX, startY, 0);
        obj.localToWorld(startWorld);
        gizmoGroup.add(createGizmo(startWorld, 0x00ff00, '__gizmo_arc_start', {
          type: "arcStart",
          arcId: obj.userData.id,
          objUUID: obj.uuid
        }));

        // End point gizmosu (açılar zaten radyan)
        const endX = center.x + radius * Math.cos(endAngle);
        const endY = center.y + radius * Math.sin(endAngle);
        const endWorld = new THREE.Vector3(endX, endY, 0);
        obj.localToWorld(endWorld);
        gizmoGroup.add(createGizmo(endWorld, 0x0000ff, '__gizmo_arc_end', {
          type: "arcEnd",
          arcId: obj.userData.id,
          objUUID: obj.uuid
        }));

        // Midpoint gizmosu (yarıçap ayarı için, arc'ın orta noktası)
        const midAngle = (startAngle + endAngle) / 2;
        const midX = center.x + radius * Math.cos(midAngle);
        const midY = center.y + radius * Math.sin(midAngle);
        const midWorld = new THREE.Vector3(midX, midY, 0);
        obj.localToWorld(midWorld);
        gizmoGroup.add(createGizmo(midWorld, 0x00ffff, '__gizmo_arc_midpoint', {
          type: "arcMidpoint",
          arcId: obj.userData.id,
          objUUID: obj.uuid
        }));
      }
    });

    // ✅ Seçili ELLIPSE'ler için gizmolar (merkez + 4 quarter point = 5 gizmo)
    scene.traverse((obj) => {
      if (obj.userData?.type === 'ellipse' && selectedObjectIds.includes(obj.userData.id)) {
        const center = obj.userData.center || { x: 0, y: 0 };
        const radiusX = obj.userData.radiusX || obj.userData.majorRadius || 0;
        const radiusY = obj.userData.radiusY || obj.userData.minorRadius || 0;
        const rotation = (obj.userData.rotation || 0) * Math.PI / 180;

        // Merkez gizmosu
        const centerWorld = new THREE.Vector3(center.x, center.y, 0);
        obj.localToWorld(centerWorld);
        gizmoGroup.add(createGizmo(centerWorld, 0xff0000, '__gizmo_ellipse_center', {
          type: "ellipseCenter",
          ellipseId: obj.userData.id,
          objUUID: obj.uuid
        }));

        // Quarter point gizmoları (rotation dahil)
        const quarterAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
        quarterAngles.forEach((angle, idx) => {
          // Ellipse parametrik formülü (rotasyon dahil)
          const cosR = Math.cos(rotation);
          const sinR = Math.sin(rotation);
          const ex = radiusX * Math.cos(angle);
          const ey = radiusY * Math.sin(angle);
          const qx = center.x + ex * cosR - ey * sinR;
          const qy = center.y + ex * sinR + ey * cosR;
          const quarterWorld = new THREE.Vector3(qx, qy, 0);
          obj.localToWorld(quarterWorld);
          gizmoGroup.add(createGizmo(quarterWorld, 0x00ffff, `__gizmo_ellipse_quarter_${idx}`, {
            type: "ellipseQuarter",
            quarterIndex: idx,
            ellipseId: obj.userData.id,
            objUUID: obj.uuid
          }));
        });
      }
    });

    // ✅ Wire gizmoları (line segment'ler için)
    scene.traverse((obj) => {
      // 🛑 Skip wire markers
      if (obj.userData?.role === 'wireMarker') return;

      // 🛑 Skip hatch pattern lines
      if (isChildOfHatch(obj)) return;

      // 🛑 Skip block children (bloklar için sadece basePoint gizmosu var)
      if (isChildOfBlock(obj)) return;

      // 🛑 Skip LineSegments (hatch patterns, etc.)
      if (obj.type === 'LineSegments') return;

      // 🛑 Skip circle, arc, ellipse, block (bunlar için özel gizmolar oluşturuldu)
      const objType = obj.userData?.type;
      if (objType === 'circle' || objType === 'arc' || objType === 'ellipse' || objType === 'block' || objType === 'mtext') return;

      if (obj.isLine && selectedObjectIds.includes(obj.userData.id)) {
        const positions = obj.geometry.attributes.position;

        // Check if wire is connected to a panel
        const wire = wires.find(w => w.id === obj.userData.id);
        const isPanelConnected = wire?.sourceId || wire?.userData?.panelID || obj.userData.sourceId || obj.userData.panelID;

        // Check if wire is a panel INPUT wire (feeding into a panel)
        const wireLayer = wire?.layer || obj.userData.layer;
        const isPanelInputWire = wireLayer === 'electricalPanel' || wireLayer === 'simpleMainPanel';

        // 🛑 Skip fixture wires (handled by useFixtureGizmo)
        if (
          wireLayer === 'lightingFixture' || 
          obj.userData.type === 'curvedLine' || 
          obj.userData.controlPoints || 
          obj.userData.function === 'wireLightingFixture'
        ) return;

        // ✅ TÜM köşe noktalarına gizmo oluştur
        for (let i = 0; i < positions.count; i++) {
          // Skip gizmo for first vertex if connected to panel
          if (i === 0 && isPanelConnected) continue;

          // Skip last two vertices for panel input wires
          if (isPanelInputWire && i >= positions.count - 2) continue;

          const x = positions.getX(i);
          const y = positions.getY(i);
          const z = positions.getZ(i);
          const point = new THREE.Vector3(x, y, z);

          // ✅ LOCAL koordinatları WORLD koordinatlarına dönüştür
          // (Blok içindeki objeler için transform uygulanması gerekiyor)
          obj.localToWorld(point);

          const geometry = new THREE.PlaneGeometry(4, 4); // 4x4 birim kare
          const material = new THREE.MeshBasicMaterial({
            color: 0x0000ff, // Mavi
            transparent: true,
            opacity: 0.8,
            depthTest: false,
          });
          const gizmo = new THREE.Mesh(geometry, material);
          gizmo.position.copy(point);
          gizmo.name = `__gizmo_vertex_${i}`;
          gizmo.renderOrder = 999;
          gizmo.userData = {
            isGizmo: true,
            type: "vertex", // Köşe noktası gizmosu
            vertexIndex: i, // Kaçıncı köşe
            lineId: obj.userData.id,
            objUUID: obj.uuid
          };
          gizmoGroup.add(gizmo);
        }

        // ✅ Kenarların orta noktalarına da gizmo ekle
        for (let i = 0; i < positions.count - 1; i++) {
          // Skip midpoint between last two vertices for panel input wires
          if (isPanelInputWire && i === positions.count - 2) continue;

          const start = new THREE.Vector3(
            positions.getX(i),
            positions.getY(i),
            positions.getZ(i)
          );
          const end = new THREE.Vector3(
            positions.getX(i + 1),
            positions.getY(i + 1),
            positions.getZ(i + 1)
          );

          // ✅ LOCAL koordinatları WORLD koordinatlarına dönüştür
          obj.localToWorld(start);
          obj.localToWorld(end);

          // Orta nokta hesapla
          const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

          const geometry = new THREE.PlaneGeometry(4, 4);
          const material = new THREE.MeshBasicMaterial({
            color: 0x00ff00, // Yeşil (orta noktaları ayırt etmek için)
            transparent: true,
            opacity: 0.8,
            depthTest: false,
          });
          const gizmo = new THREE.Mesh(geometry, material);
          gizmo.position.copy(midpoint);
          gizmo.name = `__gizmo_midpoint_${i}`;
          gizmo.renderOrder = 999;
          gizmo.userData = {
            isGizmo: true,
            type: "midpoint", // Orta nokta gizmosu
            edgeIndex: i, // Hangi kenarın ortası (i ile i+1 arası)
            lineId: obj.userData.id,
            objUUID: obj.uuid
          };
          gizmoGroup.add(gizmo);
        }
      }
    });

    scene.add(gizmoGroup);
    gizmoGroupRef.current = gizmoGroup;

    // ✅ Oluşturulan gizmolara HEMEN doğru scale'i uygula (zoom değişimini bekleme).
    // Aksi halde yeni gizmolar scale=1 (4 birim) ile doğuyor; scale-update loop sadece
    // zoom DEĞİŞİNCE çalıştığı için, zoom sabitse gizmolar yanlış boyutta kalıyordu
    // (mevcut zoom'a göre bazen büyük bazen küçük; zoom in/out yapınca düzeliyordu).
    if (camera) {
      gizmoGroup.children.forEach((gizmo) => {
        if (gizmo.userData?.type === 'mtextBorder') return;
        let s = 1;
        if (camera.isOrthographicCamera) {
          s = 2 / camera.zoom;
        } else {
          s = camera.position.distanceTo(gizmo.position) * 0.01;
        }
        gizmo.scale.set(s, s, s);
      });
    }
  }, [scene, camera, selectedObjectIds, wires, shouldShowGizmos]);
}
